import { ArrowRightIcon } from 'lucide-react';
import React, { ReactElement, useEffect, useState } from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { useCommon } from 'src/AppState/Common';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { BONUS_CREATOR_ROOT, BONUS_REWARD_ROOT } from 'src/domains/casino/utils/test-variables';
import { BonusCreationState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreation.state';
import { BonusCreationHeader } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreationHeader';
import { BonusCreationTabList } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreationTabList';
import { BonusCreationViewManager } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreationViewManager';
import { GeneralDetails } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/general-details/GeneralDetails';
import { RewardConditions } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/reward-conditions/RewardConditions';
import { Button } from 'src/domains/common/components/button/button';
import { observer } from 'src/utils/mobx-react';
import { autorun } from 'src_common/common/mobx-wrapper';

export const BonusCreation = observer('BonusCreation', (): ReactElement | null => {
    const { routerBackoffice } = useAppStateContext();
    const common = useCommon();

    // BonusCreationState should be changed to use state instead of weak map.
    const [timestamp] = useState(Date.now().toString());
    const bonusCreationIdInRoute =
        routerBackoffice.bonusCreationParams?.type === 'EDIT'
            ? routerBackoffice.bonusCreationParams.bonusCreationId
            : (routerBackoffice.bonusCreationParams?.cloneId ?? `default-id-${timestamp}`);
    const bonusCreationState = BonusCreationState.get(common, bonusCreationIdInRoute);
    const [bonusRewardState] = useState(
        () => new BonusCreationViewManager(routerBackoffice, common, bonusCreationState)
    );
    const { handlePrevView, handleNextView, currentViewIndex, bonusRewardsViewsList } = bonusRewardState;

    useEffect(() => {
        const dispose = autorun(() => {
            bonusCreationState
                .fillDataIf()
                .then()
                .catch((e) => console.error(e));
        });

        return () => {
            dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    console.log('bonusCreationIdInRoute', bonusCreationIdInRoute);
    return (
        <div
            className='flex flex-col flex-auto max-h-screen gap-6 p-6 overflow-x-hidden overflow-y-auto bg-white text-foreground flex-nowrap font-tertiary'
            data-test={BONUS_REWARD_ROOT}
            style={{ scrollbarGutter: 'stable' }}
        >
            <BonusCreationHeader
                bonusCreationId={bonusCreationIdInRoute}
                bonusRewardState={bonusRewardState}
                routerBackoffice={routerBackoffice}
            />
            <BonusCreationTabList
                bonusCreationId={bonusCreationIdInRoute}
                bonusRewardState={bonusRewardState}
            />
            {bonusCreationState.isLoading === true ||
            (bonusCreationState.bonusCreation === null && routerBackoffice.bonusCreationParams?.type === 'EDIT') ? (
                <LoadingV2
                    dataTest={BONUS_CREATOR_ROOT}
                    title='Loading bonus list...'
                />
            ) : (
                <form>
                    {routerBackoffice.bonusCreationParams?.view === 'general-details' && (
                        <GeneralDetails bonusCreationId={bonusCreationIdInRoute} />
                    )}
                    {routerBackoffice.bonusCreationParams?.view === 'reward-conditions' && (
                        <RewardConditions bonusCreationId={bonusCreationIdInRoute} />
                    )}
                </form>
            )}

            <div className='flex justify-between'>
                <Button
                    disabled={currentViewIndex <= 0}
                    icon='arrow-left'
                    onClick={handlePrevView}
                    variant='outline'
                >
                    Previous Tab
                </Button>

                <Button
                    disabled={currentViewIndex >= bonusRewardsViewsList.length - 1}
                    onClick={handleNextView}
                    variant='outline'
                >
                    Next Tab
                    <ArrowRightIcon className='size-4' />
                </Button>
            </div>
        </div>
    );
});
